import { TypeAheadFilter } from '@components/table/filters/TypeAheadFilter';
import type { TableFilterTypeAheadFilterExtendedProps } from '@components/table/filters/TypeaheadFilterProps.type';
import { DEBOUNCE_DELAYS } from '@constants/debounceDelays';

import { useEntityMetadata } from '@services/entityMetadata';
import { useSearchPaginatedUnderwriters } from '@services/underwriter/useSearchPaginatedUnderwriters';
import { handleScrollToBottom } from '@utils/HandleScrollToBottom';
import { useEffect, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { ExclamationTriangle } from '@assets/svgs';
import { Box } from '@mui/system';

export interface UnderwriterTypeaheadFilterProps extends TableFilterTypeAheadFilterExtendedProps {
  excludedUnderwriterIds?: string[];
  includeDeleted?: boolean;
}

export const UnderwriterTypeaheadFilter = (componentProps: UnderwriterTypeaheadFilterProps) => {
  const {
    disabled,
    error,
    excludedUnderwriterIds,
    fieldConfig,
    helperText,
    includeDeleted,
    label,
    onChange,
    required,
    value,
  } = componentProps;
  const { isInternalUser } = useStore((state) => state.userAuth, shallow);

  const [selectedFromList, setSelectedFromList] = useState(false);

  const [searchText, setSearchText] = useState('');
  const [isDebouncing, setIsDebouncing] = useState(false);
  const {
    underwriters,
    fetchNextPage: loadMoreUnderwriterResults,
    isLoading,
    isFetching,
    isPending,
    isFetchingNextPage,
  } = useSearchPaginatedUnderwriters({
    sort: '+user.firstName',
    pageLimit: 50,
    name: searchText,
    includeDeleted: includeDeleted ?? (isInternalUser ? true : undefined),
  });

  // this means filter has been applied by the caller but we need to ensure we have the label since this filter is paginated
  const { entityMetadata } = useEntityMetadata(
    {
      'underwriter.id': [value],
    },
    !selectedFromList && !!value ? true : false,
  );

  const selectedItemLabel = useMemo(() => {
    const uw = entityMetadata?.underwriter?.[0];
    if (uw) {
      return `${uw.label} - ${uw.helperText}`;
    }
  }, [entityMetadata]);

  // Reset the local search text when the filter value is cleared externally
  // (e.g. a "Clear all filters" action that resets `value` without going through
  // this component's own onChange), so the dropdown does not re-query stale text.
  useEffect(() => {
    if (!value) {
      setSearchText('');
    }
  }, [value]);

  const handleDebouncedInputChange = useDebouncedCallback((value) => {
    setSearchText(value);
    setIsDebouncing(false);
  }, DEBOUNCE_DELAYS.FORM_TYPING);

  const items = useMemo(
    () =>
      underwriters?.pages
        ?.flatMap((page) => page.items)
        ?.filter((ip) => !excludedUnderwriterIds?.includes(ip.id))
        ?.map((ip) => {
          return {
            key: ip.id,
            value: ip.id,
            label: `${ip.user.firstName} ${ip.user.lastName}`,
            icon: ip.deletedAt ? <ExclamationTriangle /> : undefined,
            subLabel: ip.user.email,
            sx: ip.deletedAt ? { color: 'text.subdued' } : undefined,
          };
        }) ?? [],
    [excludedUnderwriterIds, underwriters?.pages],
  );

  return (
    <Box>
      <TypeAheadFilter
        value={value}
        defaultValue={value}
        disabled={disabled}
        label={label}
        helperText={helperText}
        error={error}
        required={required}
        options={items}
        isMultiple={fieldConfig?.selectField?.multiSelect}
        limitTags={1}
        selectedItemDefaultLabel={!selectedFromList ? selectedItemLabel : undefined}
        onChange={(value: any) => {
          setSelectedFromList(true);
          setIsDebouncing(false);
          if (!value) {
            setSearchText('');
          }
          onChange(value);
        }}
        onInputChange={(_, value) => {
          setIsDebouncing(true);
          handleDebouncedInputChange(value);
        }}
        loading={isLoading || isFetching || isFetchingNextPage || isPending || isDebouncing}
        slotProps={{
          listbox: {
            onScroll: (e) => {
              handleScrollToBottom(e, loadMoreUnderwriterResults);
            },
            style: {
              maxHeight: '300px',
              backgroundColor: 'white',
            },
          },
        }}
      />
    </Box>
  );
};
